import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Link } from '@tiptap/extension-link';
import { Underline } from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Link as LinkIcon,
  Undo2,
  Redo2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Table as TableIcon,
  Trash2,
  Plus,
  Minus,
} from 'lucide-react';

const ToolbarButton = ({ onClick, active, disabled, children, label, testId }) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onClick}
    disabled={disabled}
    title={label}
    data-testid={testId}
    className={cn(
      'h-8 w-8 p-0 rounded-sm',
      active && 'bg-primary/20 text-primary',
    )}
  >
    {children}
  </Button>
);

/**
 * Rich-text editor used everywhere admins write Policy / Procedure / T&C
 * content. Wraps TipTap with a compact toolbar that exposes only the
 * formatting affordances the product needs (headings, lists, links, tables,
 * alignment). Pure-HTML in/out so the rest of the app can render it with
 * dangerouslySetInnerHTML + DOMPurify.
 */
export default function RichTextEditor({ value, onChange, placeholder, testId }) {
  const editor = useEditor({
    extensions: [
      // StarterKit ships its own Link + Underline since v3 — disable those
      // so our custom-configured versions are the single registered ones.
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { class: 'text-primary underline' },
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => { onChange?.(ed.getHTML()); },
    editorProps: {
      attributes: {
        'data-testid': testId || 'rich-text-editor',
        class: 'prose prose-invert max-w-none min-h-[300px] p-4 focus:outline-none [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:p-2 [&_th]:border [&_th]:border-border [&_th]:p-2 [&_th]:bg-muted [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg [&_a]:text-primary [&_a]:underline',
      },
    },
  });

  if (!editor) return null;

  const setLink = () => {
    const previous = editor.getAttributes('link').href;
    const url = window.prompt('URL', previous || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const TBtn = ToolbarButton;

  return (
    <div className="border border-border rounded-sm bg-background overflow-hidden" data-testid="rich-editor-wrapper">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b border-border bg-muted/30">
        <TBtn label="Heading 1" testId="rte-h1" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })}>
          <Heading1 className="w-4 h-4" />
        </TBtn>
        <TBtn label="Heading 2" testId="rte-h2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })}>
          <Heading2 className="w-4 h-4" />
        </TBtn>
        <TBtn label="Heading 3" testId="rte-h3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })}>
          <Heading3 className="w-4 h-4" />
        </TBtn>
        <div className="w-px h-5 bg-border mx-1" />
        <TBtn label="Bold" testId="rte-bold" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')}>
          <Bold className="w-4 h-4" />
        </TBtn>
        <TBtn label="Italic" testId="rte-italic" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')}>
          <Italic className="w-4 h-4" />
        </TBtn>
        <TBtn label="Underline" testId="rte-underline" onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')}>
          <UnderlineIcon className="w-4 h-4" />
        </TBtn>
        <div className="w-px h-5 bg-border mx-1" />
        <TBtn label="Bullet list" testId="rte-ul" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')}>
          <List className="w-4 h-4" />
        </TBtn>
        <TBtn label="Numbered list" testId="rte-ol" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')}>
          <ListOrdered className="w-4 h-4" />
        </TBtn>
        <div className="w-px h-5 bg-border mx-1" />
        <TBtn label="Align left" testId="rte-align-l" onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })}>
          <AlignLeft className="w-4 h-4" />
        </TBtn>
        <TBtn label="Align centre" testId="rte-align-c" onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })}>
          <AlignCenter className="w-4 h-4" />
        </TBtn>
        <TBtn label="Align right" testId="rte-align-r" onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })}>
          <AlignRight className="w-4 h-4" />
        </TBtn>
        <div className="w-px h-5 bg-border mx-1" />
        <TBtn label="Insert/Edit link" testId="rte-link" onClick={setLink} active={editor.isActive('link')}>
          <LinkIcon className="w-4 h-4" />
        </TBtn>
        <TBtn label="Insert table" testId="rte-table" onClick={insertTable}>
          <TableIcon className="w-4 h-4" />
        </TBtn>
        {editor.isActive('table') && (
          <>
            <TBtn label="Add row below" testId="rte-table-row+" onClick={() => editor.chain().focus().addRowAfter().run()}>
              <Plus className="w-3 h-3" />R
            </TBtn>
            <TBtn label="Delete row" testId="rte-table-row-" onClick={() => editor.chain().focus().deleteRow().run()}>
              <Minus className="w-3 h-3" />R
            </TBtn>
            <TBtn label="Add column right" testId="rte-table-col+" onClick={() => editor.chain().focus().addColumnAfter().run()}>
              <Plus className="w-3 h-3" />C
            </TBtn>
            <TBtn label="Delete column" testId="rte-table-col-" onClick={() => editor.chain().focus().deleteColumn().run()}>
              <Minus className="w-3 h-3" />C
            </TBtn>
            <TBtn label="Delete table" testId="rte-table-del" onClick={() => editor.chain().focus().deleteTable().run()}>
              <Trash2 className="w-4 h-4 text-destructive" />
            </TBtn>
          </>
        )}
        <div className="ml-auto flex items-center gap-0.5">
          <TBtn label="Undo" testId="rte-undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
            <Undo2 className="w-4 h-4" />
          </TBtn>
          <TBtn label="Redo" testId="rte-redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
            <Redo2 className="w-4 h-4" />
          </TBtn>
        </div>
      </div>

      {/* Editor surface */}
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
}
